/**
 * Browser mirror of functions/lib/greetingName.js, whose own source of truth is
 * email_automation/greeting_name.py in the EmailAutomation repo.
 *
 * Create React App refuses imports from outside src/, so the greeting rule the
 * browser applies when it resolves a campaign script's [NAME] at launch and the
 * greeting rule the server applies cannot share one file. They share a test
 * instead: src/utils/greetingName.test.js runs the same fixtures through both
 * and fails the moment they disagree. Change one, change the other, and change
 * the Python list they are both ports of.
 *
 * This is the file that produced the live defect of 2026-09-22: a campaign row
 * whose contact cell read exactly "Not provided" received the product's first
 * outreach email opening "Hi Not,", because the browser took the contact
 * string, split it on a space and greeted whatever came first.
 */

// Words that are a ROLE, an ORGANISATION or a CHANNEL rather than a person,
// checked at EVERY position. A contact cell containing one of these is
// describing the function ("Listing Agent", "Front Desk", "Tenant Rep") or the
// place the listing came from ("LoopNet", "Online Form"), never a human being,
// and a value that pairs a word with one of these ("Bob Rep") is describing a
// function too. Every word here is one no parent gives a child, so the
// any-position rule costs no real name; ordinary surnames that double as
// words -- Best, Price, Hope, May, Bill, April, Lee -- are deliberately NOT
// here, and must never be added.
const NON_PERSON_CONTACT_TOKENS = new Set([
  // organisations and the titles inside them
  'asset', 'broker', 'brokerage', 'colliers', 'commercial', 'company',
  'corp', 'corporation', 'cushman', 'director', 'group', 'holdings', 'inc',
  'international', 'leasing', 'llc', 'management', 'manager', 'managing',
  'office', 'owner', 'partners', 'principal', 'properties', 'property',
  'realty', 'services', 'team', 'wakefield',
  // the roles that were greeted as people -- "Hi Agent,", "Hi Landlord,",
  // "Hi Listing,", "Hi Front,", "Hi Tenant,".
  'agent', 'agents', 'landlord', 'landlords', 'tenant', 'tenants', 'rep',
  'reps', 'representative', 'listing', 'listings', 'desk', 'front',
  'reception', 'receptionist', 'sales', 'admin', 'administrator',
  'assistant', 'secretary', 'concierge', 'staff', 'department', 'dept',
  'division', 'corporate', 'realtor',
  // the CHANNEL typed where the person was asked -- "Hi Website,",
  // "Hi Online,", "Hi LoopNet,", "Hi CoStar,"
  'website', 'web', 'online', 'portal', 'form', 'forms', 'line',
  'loopnet', 'costar', 'crexi', 'zillow', 'craigslist', 'google',
  // 2026-09-23. Merging four stop lists into this one must not drop what any
  // of them caught, so every word below is one this list did not have and
  // another did. From the DASHBOARD's list
  // (src/utils/recipientVariableReview.js): "Metal Supermarkets" is its named
  // case, an organisation with no legal suffix to give it away.
  'industrial', 'holding', 'ownership', 'lp', 'supermarket', 'supermarkets',
  // From the OUTBOX SENDER's list and the model-prompt list in the backend:
  // the rest of the legal suffixes, and the firms whose names are typed into a
  // contact column when the person is unknown.
  'ltd', 'co', 'llp', 'advisors', 'advisory', 'associates', 'capital',
  'cbre', 'jll', 'savills', 'newmark',
  // BUG-F4, 2026-09-23. THE CORPORATE TITLE. "Vice President" was greeted
  // "Hi Vice,". A title is a closed vocabulary and it is the same class of
  // thing as "Listing Agent" and "Property Manager" already here: it names
  // the function, never the human. Every word below passes the test this list
  // is governed by -- no parent gives a child one of them -- so the
  // any-position rule costs no real name.
  'vice', 'president', 'chairman', 'chairwoman', 'chair', 'founder',
  'partner', 'executive', 'officer', 'supervisor', 'coordinator',
  'specialist', 'analyst', 'associate', 'intern', 'trainee',
]);

// Words that are never a person's first name, however well-formed they look.
// The list is the closing words of an email, the placeholders a human types
// into a contact column when there is no contact, and the negations and
// function words that begin a sentence someone pasted into the wrong cell. It
// is checked on the FIRST token, which is the only one that becomes a
// greeting, and on every token, because "Not written" is wrong at either
// position.
const NEVER_A_FIRST_NAME_TOKENS = new Set([
  // negations and the placeholder vocabulary of an empty field
  'not', 'no', 'none', 'nil', 'null', 'na', 'nan', 'nothing', 'blank',
  'empty', 'unknown', 'unnamed', 'unlisted', 'unavailable', 'missing',
  'tbd', 'tba', 'pending', 'unconfirmed', 'n', 'a',
  // the words an email opens and closes with
  'hi', 'hello', 'hey', 'dear', 'greetings', 'thanks', 'thank', 'thankyou',
  'regards', 'best', 'sincerely', 'cheers', 'please', 'kindly',
  // header and quoting words, which is what a pasted message fragment starts
  'from', 'to', 'cc', 'bcc', 'sent', 'subject', 'date', 're', 'fwd', 'fw',
  'reply', 'forwarded', 'original', 'message', 'sender', 'recipient',
  'email', 'mail', 'phone', 'tel', 'mobile', 'contact',
  // the function words a sentence fragment begins with
  'the', 'this', 'that', 'these', 'those', 'there', 'their', 'they', 'it',
  'and', 'but', 'for', 'with', 'without', 'all', 'any', 'see', 'attached',
  'info', 'information', 'details', 'yes', 'ok', 'okay', 'test', 'sample',
  'unassigned', 'vacant', 'available', 'general', 'info@', 'same',
  // the honorific typed in front of the name, which is not the name:
  // "Mr Smith" was greeted "Hi Mr,".
  'mr', 'mrs', 'ms', 'miss', 'mx', 'dr', 'prof', 'professor', 'sir',
  'madam', 'madame', 'mister', 'attn', 'atten', 'attention',
  // the INSTRUCTION an advisor leaves their future self in a contact column
  'ask', 'call', 'calling', 'called', 'text', 'texted', 'texting',
  'emailed', 'emailing', 'via', 'per', 'check', 'find', 'need', 'needs',
  'new', 'use', 'try', 'inquire', 'multiple', 'various', 'ditto',
  'duplicate', 'misc', 'other',
  // how to reach them, answered where WHO was asked
  'direct', 'cell', 'main', 'head', 'site', 'onsite', 'voicemail', 'vm',
  // the state of the last attempt, and of the contact
  'skip', 'ignore', 'later', 'left', 'out', 'on', 'do', 'dont', 'wrong',
  'disconnected', 'gone', 'moved', 'retired', 'deceased', 'former', 'old',
  'temp',
  // the shorthands, which only survive lower-cased or title-cased -- an
  // all-caps "PM"/"LL"/"DNC"/"OOO" is already refused by the shape rule
  'pm', 'll', 'dnc', 'ooo', 'unk', 'unkn',
  // BUG-F4, 2026-09-23. THE AUTOMATED MAILBOX, which reaches this rule
  // through the FROM DISPLAY NAME rather than a contact cell: an
  // out-of-office banner answers our outreach and its display name becomes
  // the person we greet. "Automatic reply" was greeted "Hi Automatic," and
  // "No-Reply" survived the shape rule whole ("noreply" after normalisation)
  // to be greeted "Hi No-Reply,". These are the names machines send under,
  // and no human is called any of them.
  'automatic', 'auto', 'autoreply', 'autoresponder', 'noreply',
  'donotreply', 'mailer', 'mailerdaemon', 'daemon', 'postmaster',
  'notification', 'notifications', 'alert', 'alerts', 'system', 'robot',
  'bot', 'bounce', 'undeliverable', 'undelivered', 'delivery',
]);

// The unambiguous half of the list above, for the second token of a two-word
// contact value. Narrow on purpose: an ordinary surname must not be swept up.
const PLACEHOLDER_SECOND_TOKENS = new Set([
  'written', 'provided', 'listed', 'known', 'unknown', 'available',
  'unavailable', 'na', 'nan', 'null', 'nil', 'none', 'tbd', 'tba', 'blank',
  'empty', 'missing', 'pending', 'unconfirmed', 'specified', 'given',
  'assigned', 'found', 'applicable', 'yet',
]);

/**
 * The first name to greet, or null when nothing in the value is trustworthy.
 *
 * @param {unknown} contactName the contact cell, as typed by a human
 * @returns {string|null} the first name, or null for a neutral greeting
 */
function safeGreetingFirstName(contactName) {
  if (typeof contactName !== 'string') return null;
  const candidate = contactName.trim();
  if (!candidate || candidate.includes(',') || candidate.includes('@')) return null;
  // A digit ANYWHERE means the cell holds a suite, a phone number or a row
  // reference alongside whatever else -- not a person to greet.
  if (/\d/.test(candidate)) return null;

  const parts = candidate.split(/\s+/).filter(Boolean);
  if (parts.length === 0 || parts.length > 2) return null;

  const tokens = parts.map(part => part.toLowerCase().replace(/[^a-z]/g, ''));
  // A role, an organisation or a channel word ANYWHERE in the value condemns
  // the whole value: "Listing Agent" and "Front Desk" put it second, "Agent
  // Smith" puts it first, and neither is a person to greet.
  if (tokens.some(token => NON_PERSON_CONTACT_TOKENS.has(token))) return null;
  // The wall on the token that actually becomes the greeting: honorifics
  // ("Mr Smith"), instructions ("Ask Tom", "Check LoopNet") and the
  // empty-field vocabulary ("Not written").
  if (NEVER_A_FIRST_NAME_TOKENS.has(tokens[0])) return null;
  // A placeholder in the SECOND position condemns the value too -- "Contact
  // unknown" and "Broker tbd" reach here with a plausible-looking first token.
  if (tokens.length > 1 && PLACEHOLDER_SECOND_TOKENS.has(tokens[1])) return null;

  const firstName = parts[0];
  // An all-caps token is a firm's initials, not a person.
  if (firstName.length > 1
    && firstName === firstName.toUpperCase()
    && firstName !== firstName.toLowerCase()) return null;
  if (!/^[A-Za-z][A-Za-z.'-]{0,63}$/.test(firstName)) return null;
  // CAPITALISED AND ALPHABETIC, which is the shape rule the placeholder also
  // passed -- the stop-list above is the load-bearing half. This refuses the
  // family of fragments that are not names in any casing: "written", "n/a",
  // "x", "3rd". Internal capitals stay legal so McKenzie, DeShawn, O'Brien and
  // Jean-Luc are still greeted by name.
  if (!/^[A-Z][A-Za-z]*(?:[.'-][A-Za-z][A-Za-z]*)*$/.test(firstName)) return null;
  // A lone initial is not a name to greet.
  if (firstName.replace(/[^A-Za-z]/g, '').length < 2) return null;
  return firstName;
}

export {
  NON_PERSON_CONTACT_TOKENS,
  NEVER_A_FIRST_NAME_TOKENS,
  PLACEHOLDER_SECOND_TOKENS,
  safeGreetingFirstName,
};
